import { useCallback, useReducer } from 'react';
import { isNotDefined } from '@togglecorp/fujs';
import { accumulateDifferentialErrors, accumulateErrors, analyzeErrors, accumulateValues } from './schema.js';
import { isCallable } from './utils.js';

function useForm(schema, initialState, context) {
  const {
    value: initialFormValue,
    error: initialError,
    pristine: initialPristine = true
  } = initialState;
  const formReducer = useCallback((prevState, action) => {
    if (action.type === 'CREATE_RESTORE_POINT') {
      return { ...prevState,
        hasRestorePoint: true,
        restorePointValue: prevState.value,
        restorePointError: prevState.error,
        restorePointPristine: prevState.pristine
      };
    }

    if (action.type === 'RESTORE_RESTORE_POINT') {
      if (!prevState.hasRestorePoint) {
        return prevState;
      }

      if (action.retain) {
        return { ...prevState,
          value: prevState.restorePointValue,
          error: prevState.restorePointError,
          pristine: prevState.restorePointPristine
        };
      }

      return {
        hasRestorePoint: false,
        value: prevState.restorePointValue,
        error: prevState.restorePointError,
        pristine: prevState.restorePointPristine
      };
    }

    if (action.type === 'CLEAR_RESTORE_POINT') {
      if (!prevState.hasRestorePoint) {
        return prevState;
      }

      return {
        hasRestorePoint: false,
        value: prevState.value,
        error: prevState.error,
        pristine: prevState.pristine
      };
    }

    if (action.type === 'SET_PRISTINE') {
      const {
        value
      } = action;
      return { ...prevState,
        pristine: value
      };
    }

    if (action.type === 'SET_ERROR') {
      const {
        error: errorFromAction
      } = action;
      const newErr = isCallable(errorFromAction) ? errorFromAction(prevState.error) : errorFromAction;
      return { ...prevState,
        error: newErr
      };
    }

    if (action.type === 'SET_VALUE') {
      const {
        value: valueFromAction,
        partialUpdate
      } = action;
      const newValue = isCallable(valueFromAction) ? valueFromAction(prevState.value) : valueFromAction;

      if (partialUpdate) {
        const oldError = prevState.error;
        const oldValue = prevState.value;

        if (oldValue === newValue) {
          return prevState;
        }

        const newError = accumulateDifferentialErrors(oldValue, newValue, oldError, schema, newValue, context, false);
        return { ...prevState,
          value: newValue,
          error: newError,
          pristine: false
        };
      }

      return { ...prevState,
        value: newValue,
        error: undefined,
        pristine: true
      };
    }

    if (action.type === 'SET_VALUE_FIELD') {
      const {
        key,
        value: valueFromAction
      } = action;
      const oldValue = prevState.value;
      const oldError = prevState.error;
      const newVal = isCallable(valueFromAction) ? valueFromAction(oldValue[key]) : valueFromAction; // NOTE: just don't set anything if the value is not really changed

      if (oldValue[key] === newVal) {
        return prevState;
      }

      const newValue = { ...oldValue,
        [key]: newVal
      };
      const newError = accumulateDifferentialErrors(oldValue, newValue, oldError, schema, newValue, context, false);
      return { ...prevState,
        value: newValue,
        error: newError,
        pristine: false
      };
    } // eslint-disable-next-line no-console


    console.error('Action is not supported');
    return prevState;
  }, [schema, context]);
  const [state, dispatch] = useReducer(formReducer, {
    value: initialFormValue,
    error: initialError,
    pristine: initialPristine,
    hasRestorePoint: false
  });
  const createRestorePoint = useCallback(() => {
    const action = {
      type: 'CREATE_RESTORE_POINT'
    };
    dispatch(action);
  }, []);
  const restore = useCallback(function () {
    let retain = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    const action = {
      type: 'RESTORE_RESTORE_POINT',
      retain
    };
    dispatch(action);
  }, []);
  const clearRestorePoint = useCallback(() => {
    const action = {
      type: 'CLEAR_RESTORE_POINT'
    };
    dispatch(action);
  }, []);
  const setPristine = useCallback(pristineValue => {
    const action = {
      type: 'SET_PRISTINE',
      value: pristineValue
    };
    dispatch(action);
  }, []);
  const setError = useCallback(errors => {
    const action = {
      type: 'SET_ERROR',
      error: errors
    };
    dispatch(action);
  }, []);
  const setValue = useCallback((value, partialUpdate) => {
    const action = {
      type: 'SET_VALUE',
      value,
      partialUpdate
    };
    dispatch(action);
  }, []);
  const setFieldValue = useCallback(function () {
    for (var _len = arguments.length, entries = new Array(_len), _key = 0; _key < _len; _key++) {
      entries[_key] = arguments[_key];
    }

    const [value, key] = entries;
    const action = {
      type: 'SET_VALUE_FIELD',
      key,
      value
    };
    dispatch(action);
  }, []);
  const validate = useCallback(accumulateOnError => {
    const stateErrors = accumulateErrors(state.value, schema, state.value, context);
    const stateErrored = analyzeErrors(stateErrors);

    if (stateErrored) {
      const value = accumulateOnError ? accumulateValues(state.value, schema, state.value, context, {
        nullable: true
      }) : undefined;
      return {
        errored: true,
        error: stateErrors,
        value
      };
    } // NOTE: server needs `null` to identify that the value is not defined


    const validatedValues = accumulateValues(state.value, schema, state.value, context, {
      nullable: true
    });
    return {
      errored: false,
      value: validatedValues,
      error: undefined
    };
  }, [schema, state, context]);
  return {
    value: state.value,
    error: state.error,
    pristine: state.pristine,
    setError,
    setValue,
    setFieldValue,
    setPristine,
    validate,
    hasRestorePoint: state.hasRestorePoint,
    restorePointValue: state.hasRestorePoint ? state.restorePointValue : state.value,
    restorePointError: state.hasRestorePoint ? state.restorePointError : state.error,
    restorePointPristine: state.hasRestorePoint ? state.restorePointPristine : state.pristine,
    createRestorePoint,
    restore,
    clearRestorePoint
  };
}

function useFormObject(name, onChange, defaultValue) {
  const setFieldValue = useCallback(function () {
    for (var _len2 = arguments.length, entries = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      entries[_key2] = arguments[_key2];
    }

    // NOTE: may need to cast callableValue here
    const [callableValue, key] = entries;
    onChange(oldValue => {
      const baseValue = oldValue ?? (isCallable(defaultValue) ? defaultValue() : defaultValue);
      return { ...baseValue,
        [key]: isCallable(callableValue) ? callableValue(baseValue[key]) : callableValue
      };
    }, name);
  }, [name, defaultValue, onChange]);
  return setFieldValue;
}
function useFormArray(name, onChange) {
  const setValue = useCallback((val, index) => {
    onChange(oldValue => {
      const newVal = [...(oldValue ?? [])];

      if (isNotDefined(index)) {
        newVal.push(isCallable(val) ? val(undefined) : val);
      } else {
        newVal[index] = isCallable(val) ? val(newVal[index]) : val;
      }

      return newVal;
    }, name);
  }, [name, onChange]);
  const removeValue = useCallback(index => {
    onChange(oldValue => {
      if (!oldValue) {
        return [];
      }

      const newVal = [...oldValue];
      newVal.splice(index, 1);
      return newVal;
    }, name);
  }, [name, onChange]);
  return {
    setValue,
    removeValue
  };
}

export { useForm as default, useFormArray, useFormObject };
//# sourceMappingURL=form.js.map
